import type { FoodSpot } from "../types";

// Coordinates are approximate, centred on Kozhikode city.
// Refine from a map if you need pin-point accuracy for navigation.
export const SPOTS: FoodSpot[] = [
  {
    id: "paragon",
    name: "Paragon Restaurant",
    type: "Restaurant",
    cuisine: "Malabar",
    area: "SM Street",
    description:
      "A Kozhikode institution since 1939, serving classic Malabar fare in a no-frills dining hall that locals have trusted for generations.",
    rating: 4.5,
    priceRange: "$$",
    specialties: ["Malabar Biryani", "Mutton Stew", "Ari Irachi", "Pathiri"],
    coordinates: { lat: 11.2588, lng: 75.7804 },
    hours: "7:00 AM - 11:00 PM",
    phone: "+91 495 272 0111",
    recommendations:
      "Order the mutton biryani before noon on weekends - it sells out by lunch.",
  },
  {
    id: "zains",
    name: "Zain's Hotel",
    type: "Restaurant",
    cuisine: "Arab-Malabar",
    area: "Palayam",
    description:
      "The reference point for Malabar Mandhi in the city. Smoky, tender rice and slow-cooked meat served on a shared platter, as it is meant to be eaten.",
    rating: 4.6,
    priceRange: "$$",
    specialties: ["Mutton Mandhi", "Chicken Mandhi", "Khuboos", "Shawi"],
    coordinates: { lat: 11.2606, lng: 75.7761 },
    hours: "11:00 AM - 11:30 PM",
    recommendations:
      "Go early for dinner - the smoky Mandhi platter is best fresh off the fire.",
  },
  {
    id: "sm-street",
    name: "SM Street Food Lane",
    type: "Street Food",
    cuisine: "Malabar Street",
    area: "Mittai Theruvu",
    description:
      "The candy-and-snack street of old Calicut, where stalls line up shoulder to shoulder selling sulaimani, halwa, banana chips, and fresh-cut fruit.",
    rating: 4.4,
    priceRange: "$",
    specialties: ["Sulaimani Tea", "Kozhikode Halwa", "Banana Chips", "Pazham"],
    coordinates: { lat: 11.2525, lng: 75.7768 },
    hours: "8:00 AM - 10:30 PM",
    recommendations:
      "Walk end to end before buying - the same item varies subtly from stall to stall.",
  },
  {
    id: "sagar",
    name: "Sagar Hotel",
    type: "Restaurant",
    cuisine: "South Indian Vegetarian",
    area: "Mavoor Road",
    description:
      "Clean, fast vegetarian thali and dosa service that has hosted Kozhikode families for decades. Reliable breakfast, reliable lunch, no surprises.",
    rating: 4.2,
    priceRange: "$",
    specialties: ["Masala Dosa", "Vegetable Thali", "Idli Sambar", "Filter Coffee"],
    coordinates: { lat: 11.2515, lng: 75.7838 },
    hours: "6:30 AM - 10:00 PM",
    recommendations:
      "Breakfast between 7 AM and 9 AM is the freshest and least crowded slot.",
  },
  {
    id: "rahmath",
    name: "Hotel Rahmath",
    type: "Restaurant",
    cuisine: "Malabar",
    area: "Kuttichira",
    description:
      "A neighbourhood stalwart for Malabar meals - fish curry, pathiri, and beef fry cooked in the homestyle Kozhikode way without any fuss.",
    rating: 4.3,
    priceRange: "$",
    specialties: ["Fish Curry", "Beef Fry", "Pathiri", "Coconut Rice"],
    coordinates: { lat: 11.2455, lng: 75.7722 },
    hours: "7:00 AM - 10:00 PM",
    recommendations:
      "Try the fish curry with pathiri - the bread soaks up the gravy the right way.",
  },
  {
    id: "coral",
    name: "Coral Beach Restaurant",
    type: "Beach Shack",
    cuisine: "Seafood",
    area: "Kozhikode Beach",
    description:
      "Open-air seating on the promenade, fresh catch of the day grilled or curried, with the Arabian Sea as the only decoration you need.",
    rating: 4.1,
    priceRange: "$$",
    specialties: ["Grilled Fish", "Prawn Curry", "Calamari Fry", "Fish Pakora"],
    coordinates: { lat: 11.2480, lng: 75.7750 },
    hours: "11:00 AM - 10:30 PM",
    recommendations:
      "Sunset tables go fast - arrive by 5:30 PM to grab a sea-facing seat.",
  },
  {
    id: "topform",
    name: "Topform Restaurant",
    type: "Beach Shack",
    cuisine: "Indian Chinese & Seafood",
    area: "Kozhikode Beach",
    description:
      "Beach-facing comfort food - the kind of place where families order chicken fried rice, fish fingers, and milkshakes in the same meal and it works.",
    rating: 4.0,
    priceRange: "$$",
    specialties: ["Chicken Fried Rice", "Fish Fingers", "Milkshake", "Gobi Manchurian"],
    coordinates: { lat: 11.2490, lng: 75.7760 },
    hours: "10:00 AM - 11:00 PM",
    recommendations:
      "Pair the fried rice with a fresh juice from the stall next door - locals do exactly that.",
  },
  {
    id: "kondu",
    name: "Kondu Beach Restaurant",
    type: "Beach Shack",
    cuisine: "Seafood",
    area: "Kozhikode Beach",
    description:
      "Smaller and quieter than its neighbours, with a short seafood menu that leans on whatever the boats brought in that morning.",
    rating: 4.2,
    priceRange: "$$",
    specialties: ["Fish Curry", "Karimeen Pollichathu", "Prawn Roast"],
    coordinates: { lat: 11.2472, lng: 75.7728 },
    hours: "11:30 AM - 10:00 PM",
    recommendations:
      "Ask for the catch of the day - it is not on the printed menu.",
  },
  {
    id: "indibar",
    name: "Indibar Parotta Spot",
    type: "Street Food",
    cuisine: "Malabar",
    area: "Palayam",
    description:
      "Late-evening parotta and beef fry counter that runs into the night, where the rotation of dough on the hot plate is the whole show.",
    rating: 4.3,
    priceRange: "$",
    specialties: ["Parotta", "Beef Roast", "Egg Roast", "Kerala Porotta"],
    coordinates: { lat: 11.2610, lng: 75.7770 },
    hours: "4:00 PM - 1:00 AM",
    recommendations:
      "Best after 9 PM - the griddle is hot and the crowd keeps the staff honest.",
  },
  {
    id: "albake",
    name: "Al-Bake Shawarma",
    type: "Street Food",
    cuisine: "Middle Eastern",
    area: "Mavoor Road",
    description:
      "Quick shawarma and grilled chicken counter that has been a post-work stop for university students for years.",
    rating: 4.2,
    priceRange: "$",
    specialties: ["Chicken Shawarma", "Alfaham", "Grilled Chicken", "Mandi Rice"],
    coordinates: { lat: 11.2530, lng: 75.7850 },
    hours: "11:00 AM - 12:00 AM",
    recommendations:
      "Get the shawarma rolled, not plated - it travels and eats better.",
  },
  {
    id: "malabar-heritage",
    name: "Hotel Malabar Heritage",
    type: "Restaurant",
    cuisine: "Malabar",
    area: "Koduvally Road",
    description:
      "A sit-down option for traditional Malabar meals served on banana leaf, with a heavier focus on slow-cooked meat than the average Paragon visit.",
    rating: 4.3,
    priceRange: "$$",
    specialties: ["Malabar Meals", "Mutton Kuruma", "Coconut Pathiri", "Irachi Puttu"],
    coordinates: { lat: 11.2570, lng: 75.7890 },
    hours: "11:00 AM - 10:30 PM",
    recommendations:
      "Ask for the daily fish curry - it is never on the printed menu and always better.",
  },
  {
    id: "hafis-falooda",
    name: "Hafis Falooda & Juice",
    type: "Beverages",
    cuisine: "Desserts & Drinks",
    area: "Mittalai Theruvu",
    description:
      "A small counter on the sweet street that pours thick falooda and fresh juices - the right end to any SM Street snack crawl.",
    rating: 4.1,
    priceRange: "$",
    specialties: ["Falooda", "Sharbat", "Fresh Lime Soda", "Milkshake"],
    coordinates: { lat: 11.2528, lng: 75.7775 },
    hours: "10:00 AM - 11:00 PM",
    recommendations:
      "Order the rose falooda after the savoury crawl - it is the local ritual.",
  },
];

export const CUISINES = Array.from(new Set(SPOTS.map((s) => s.cuisine))).sort();
export const AREAS = Array.from(new Set(SPOTS.map((s) => s.area))).sort();
export const TYPES = Array.from(new Set(SPOTS.map((s) => s.type))).sort();